import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';

import {
    ComissaoAssinanteResponse,
    ComissaoCargoAssinanteResponse,
    ComissaoConfiguracaoRequest,
    ComissaoConfiguracaoResponse,
    ComissaoLocalReuniaoConfiguracaoResponse,
    ComissaoLocalReuniaoRequest,
    ComissaoModeloAtaConfiguracaoResponse
} from '@/core/api/sicom-api/comissoes/configuracoes/comissao-configuracao.model';
import { ComissaoConfiguracaoService } from '@/core/api/sicom-api/comissoes/configuracoes/comissao-configuracao.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { ComissaoAssuntoResponse } from '@/core/api/sicom-api/comissoes/configuracoes/comissao-assunto.model';
import { EstruturaResponse } from '@/core/api/sicom-api/sistema/estrutura/estrutura.model';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { TableViewSwitcherComponent } from '@/shared/components/layout/table-view-switcher/table-view-switcher.component';
import { SelecionaAssunto } from '@/shared/components/seleciona-assunto/seleciona-assunto';
import { SelecionaEstrutura } from '@/shared/components/seleciona-estrutura/seleciona-estrutura';
import { LayoutService } from '@/layout/service/layout.service';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { Toast } from '@/shared/components/toast/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import { FiltroCampo } from '@/shared/components/layout/filtros-bar/filtro-campo';
import { FiltrosBar } from '@/shared/components/layout/filtros-bar/filtros-bar';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type AbaConfiguracaoComissao = 'geral' | 'emails' | 'locais' | 'modelos';
type CampoCorpoEmail = 'corpoEmailConvocacoes' | 'corpoEmailConvites' | 'corpoEmailCancelamentos';

interface ComissaoConfiguracaoForm {
    idEstruturaOrganizacional: number | null;
    dsEstruturaOrganizacional: string;
    nmEmailRemetente: string;
    senhaEmailRemetente: string;
    limparSenhaEmail: boolean;
    idCargoAssinante: number | null;
    dsCargoAssinante: string;
    idSolicitanteAssinante: number | null;
    ufSgUfAssinante: string;
    nmSolicitanteAssinante: string;
    nmEmailsCopia: string;
    copiaConvocacoes: boolean;
    copiaConvites: boolean;
    copiaConvitesOficiados: boolean;
    copiaCancelamentos: boolean;
    tituloEmailConvocacoes: string;
    corpoEmailConvocacoes: string;
    tituloEmailConvites: string;
    corpoEmailConvites: string;
    tituloEmailCancelamentos: string;
    corpoEmailCancelamentos: string;
    autosave: boolean;
    temporizadorAutosave: number | null;
    idAssuntoAta: string;
    dsAssuntoAta: string;
}

interface EditorEmailState {
    visivel: boolean;
    titulo: string;
    campo: CampoCorpoEmail | null;
    texto: string;
}

interface LocalReuniaoForm {
    idLocalReuniao: number | null;
    dsLocalReuniao: string;
    inInternoExterno: string;
    ativo: boolean;
}

interface LocalSortOpcao {
    label: string;
    field: 'dsLocalReuniao' | 'idLocalReuniao';
    direction: 'asc' | 'desc';
}

@Component({
    selector: 'app-configuracoes-comissoes',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        CommonModule,
        FormsModule,
        ButtonModule,
        CardModule,
        CanDirective,
        DialogModule,
        InputNumberModule,
        InputTextModule,
        PaginatorModule,
        SelectModule,
        SelectButtonModule,
        TableModule,
        TagModule,
        TextareaModule,
        Toast,
        ToggleSwitchModule,
        TooltipModule,
        CardContent,
        CardContainer,
        TableViewSwitcherComponent,
        SelecionaAssunto,
        SelecionaEstrutura,
        FiltrosBar,
        FiltroCampo
    ],
    templateUrl: './configuracoes-comissoes.html',
    styleUrl: './configuracoes-comissoes.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Ajustes gerais das comissões: parâmetros, editor de e-mail e locais de comissão (grade + modais). */
export class ConfiguracoesComissoes implements OnInit {
    private readonly configuracaoService = inject(ComissaoConfiguracaoService);
    private readonly messageService = inject(MessageService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly layoutService = inject(LayoutService);
    readonly seletorAssunto = viewChild(SelecionaAssunto);
    readonly seletorEstrutura = viewChild(SelecionaEstrutura);

    protected readonly roleEditar = PERMISSOES.comissoes.configuracoes.editar;
    protected readonly roleGerenciarLocais = PERMISSOES.comissoes.configuracoes.gerenciarLocais;

    readonly abas = [
        { label: 'Geral', value: 'geral' },
        { label: 'E-mails', value: 'emails' },
        { label: 'Locais', value: 'locais' },
        { label: 'Modelos', value: 'modelos' }
    ];

    readonly tiposLocal = [
        { label: 'Interno', value: 'I' },
        { label: 'Externo', value: 'E' }
    ];

    readonly carregando = signal(false);
    readonly salvando = signal(false);
    readonly carregandoCargos = signal(false);
    readonly carregandoAssinante = signal(false);
    readonly carregandoModelos = signal(false);
    readonly carregandoLocais = signal(false);
    readonly salvandoLocal = signal(false);
    readonly cargosAssinante = signal<ComissaoCargoAssinanteResponse[]>([]);
    readonly modelosAta = signal<ComissaoModeloAtaConfiguracaoResponse[]>([]);
    readonly locaisReuniao = signal<ComissaoLocalReuniaoConfiguracaoResponse[]>([]);
    readonly senhaConfigurada = signal(false);
    readonly documentoDisponivelCount = computed(() => this.modelosAta().filter((modelo) => modelo.documentoDisponivel).length);
    readonly modelosAtivosCount = computed(() => this.modelosAta().filter((modelo) => modelo.ativo).length);
    readonly locaisAtivosCount = computed(() => this.locaisReuniao().filter((local) => local.ativo).length);

    // ── Locais: visualização, filtros, ordenação e paginação (padrão refis/pagamento) ──
    readonly locaisView = signal<'table' | 'card'>(this.layoutService.getPresentationType());
    readonly filtroLocalTexto = signal('');
    readonly filtroLocalTextoAplicado = signal('');
    readonly filtroLocalTipo = signal<'' | 'I' | 'E'>('');
    readonly filtroLocalTipoAplicado = signal<'' | 'I' | 'E'>('');
    readonly filtroLocalSituacao = signal<'' | 'ativos' | 'inativos'>('');
    readonly filtroLocalSituacaoAplicada = signal<'' | 'ativos' | 'inativos'>('');
    readonly localSortField = signal<LocalSortOpcao['field']>('dsLocalReuniao');
    readonly localSortOrder = signal<1 | -1>(1);

    readonly localSortOpcoes: LocalSortOpcao[] = [
        { label: 'Local (A-Z)', field: 'dsLocalReuniao', direction: 'asc' },
        { label: 'Local (Z-A)', field: 'dsLocalReuniao', direction: 'desc' },
        { label: 'Código (crescente)', field: 'idLocalReuniao', direction: 'asc' },
        { label: 'Código (decrescente)', field: 'idLocalReuniao', direction: 'desc' }
    ];
    readonly localSortSelecionado = signal<LocalSortOpcao>(this.localSortOpcoes[0]);

    readonly tiposLocalFiltro = [
        { label: 'Todos', value: '' },
        { label: 'Interno', value: 'I' },
        { label: 'Externo', value: 'E' }
    ];
    readonly situacoesLocalFiltro = [
        { label: 'Todas', value: '' },
        { label: 'Ativos', value: 'ativos' },
        { label: 'Inativos', value: 'inativos' }
    ];

    readonly localFirst = signal(0);
    readonly localRows = signal(10);

    readonly locaisFiltrados = computed<ComissaoLocalReuniaoConfiguracaoResponse[]>(() => {
        const termo = this.filtroLocalTextoAplicado().trim().toLowerCase();
        const tipo = this.filtroLocalTipoAplicado();
        const situacao = this.filtroLocalSituacaoAplicada();
        const field = this.localSortField();
        const order = this.localSortOrder();

        const filtrados = this.locaisReuniao().filter((local) => {
            const nome = (local.dsLocalReuniao ?? '').toLowerCase();
            if (termo && !nome.includes(termo)) return false;
            if (tipo && this.getTipoLocal(local) !== tipo) return false;
            if (situacao === 'ativos' && !local.ativo) return false;
            if (situacao === 'inativos' && local.ativo) return false;
            return true;
        });

        return filtrados.sort((a, b) => {
            const va = a[field];
            const vb = b[field];
            let cmp: number;
            if (typeof va === 'number' && typeof vb === 'number') {
                cmp = va - vb;
            } else {
                cmp = String(va ?? '').localeCompare(String(vb ?? ''), 'pt-BR');
            }
            return cmp * order;
        });
    });

    readonly totalLocais = computed(() => this.locaisFiltrados().length);
    readonly locaisPagina = computed<ComissaoLocalReuniaoConfiguracaoResponse[]>(() => {
        const inicio = this.localFirst();
        return this.locaisFiltrados().slice(inicio, inicio + this.localRows());
    });

    readonly abaAtiva = signal<AbaConfiguracaoComissao>('geral');
    readonly form = signal<ComissaoConfiguracaoForm>(this.criarFormPadrao());
    editorEmail: EditorEmailState = this.criarEditorEmailPadrao();
    readonly localDialogVisivel = signal(false);
    localForm: LocalReuniaoForm = this.criarLocalFormPadrao();
    readonly excluirLocalDialogVisivel = signal(false);
    readonly localParaExcluir = signal<ComissaoLocalReuniaoConfiguracaoResponse | null>(null);

    ngOnInit(): void {
        this.carregarConfiguracao();
        this.carregarCargosAssinante();
        this.carregarLocaisReuniao();
        // this.carregarModelosAta(); // Desativado: fluxo consulta SCCT_MODELO_ATA.
    }

    /** Atualiza um campo do formulário de configuração (two-way manual dos `[ngModel]` do template). */
    patchForm<K extends keyof ComissaoConfiguracaoForm>(campo: K, valor: ComissaoConfiguracaoForm[K]): void {
        this.form.update((f) => ({ ...f, [campo]: valor }));
    }

    carregarConfiguracao(): void {
        const idEstrutura = this.validarEstrutura();
        if (!idEstrutura) {
            return;
        }

        this.carregando.set(true);
        this.configuracaoService
            .consultar(idEstrutura)
            .pipe(
                finalize(() => this.carregando.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((configuracao) => this.aplicarConfiguracao(configuracao));
    }

    salvarConfiguracao(): void {
        const idEstrutura = this.validarEstrutura();
        if (!idEstrutura || !this.validarAutosave()) {
            return;
        }

        this.salvando.set(true);
        this.configuracaoService
            .salvar(idEstrutura, this.criarRequest())
            .pipe(
                finalize(() => this.salvando.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((configuracao) => {
                this.aplicarConfiguracao(configuracao);
                this.messageService.add({ severity: 'success', summary: 'Configurações', detail: 'Configurações salvas com sucesso.' });
            });
    }

    carregarCargosAssinante(): void {
        this.carregandoCargos.set(true);
        this.configuracaoService
            .listarCargosAssinante()
            .pipe(
                finalize(() => this.carregandoCargos.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((cargos) => this.cargosAssinante.set(cargos ?? []));
    }

    selecionarCargoAssinante(idCargo: number | null): void {
        this.form.update((f) => ({
            ...f,
            idCargoAssinante: idCargo,
            dsCargoAssinante: this.cargosAssinante().find((cargo) => cargo.idCargo === idCargo)?.dsCargo ?? '',
            idSolicitanteAssinante: null,
            ufSgUfAssinante: '',
            nmSolicitanteAssinante: ''
        }));

        if (!idCargo) {
            return;
        }

        this.carregandoAssinante.set(true);
        this.configuracaoService
            .buscarAssinantePorCargo(idCargo)
            .pipe(
                finalize(() => this.carregandoAssinante.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((assinante) => this.aplicarAssinante(assinante));
    }

    carregarModelosAta(): void {
        this.modelosAta.set([]);
        this.carregandoModelos.set(false);
        /*
        Chamada desativada temporariamente: endpoint consulta a tabela SCCT_MODELO_ATA.
        this.carregandoModelos.set(true);
        this.configuracaoService
            .listarModelosAta()
            .pipe(
                finalize(() => this.carregandoModelos.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((modelos) => this.modelosAta.set(modelos ?? []));
        */
    }

    alterarStatusModelo(modelo: ComissaoModeloAtaConfiguracaoResponse, ativo: boolean): void {
        const idModeloAta = modelo.idModeloAta;
        if (!idModeloAta) {
            return;
        }

        modelo.ativo = !ativo;
        this.modelosAta.set([...this.modelosAta()]);
        this.messageService.add({
            severity: 'warn',
            summary: 'Modelo de ata',
            detail: 'Alteração de modelos de ata desativada temporariamente.'
        });
        /*
        Chamada desativada temporariamente: endpoint atualiza a tabela SCCT_MODELO_ATA.
        modelo.ativo = ativo;
        this.modelosAta.set([...this.modelosAta()]);
        this.configuracaoService
            .atualizarStatusModeloAta(idModeloAta, { ativo })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Modelo de ata',
                        detail: ativo ? 'Modelo ativado com sucesso.' : 'Modelo desativado com sucesso.'
                    });
                },
                error: () => {
                    modelo.ativo = !ativo;
                    this.modelosAta.set([...this.modelosAta()]);
                }
            });
        */
    }

    abrirEditorEmail(campo: CampoCorpoEmail, titulo: string): void {
        this.editorEmail = {
            visivel: true,
            titulo,
            campo,
            texto: this.form()[campo] ?? ''
        };
    }

    salvarEditorEmail(): void {
        const campo = this.editorEmail.campo;
        if (campo) {
            this.patchForm(campo, this.editorEmail.texto ?? '');
        }
        this.editorEmail = this.criarEditorEmailPadrao();
    }

    abrirSeletorAssunto(): void {
        this.seletorAssunto()?.abrir();
    }

    onAssuntoSelecionado(assunto: ComissaoAssuntoResponse): void {
        this.form.update((f) => ({ ...f, idAssuntoAta: assunto.idAssunto ?? '', dsAssuntoAta: assunto.dsAssunto ?? '' }));
    }

    assuntoAtaLabel(): string {
        const config = this.form();
        const codigo = (config.idAssuntoAta ?? '').trim();
        if (!codigo) {
            return '';
        }
        const descricao = (config.dsAssuntoAta ?? '').trim();
        return descricao ? `${codigo} — ${descricao}` : codigo;
    }

    abrirSeletorEstrutura(): void {
        this.seletorEstrutura()?.abrir();
    }

    onEstruturaSelecionada(estrutura: EstruturaResponse): void {
        this.form.update((f) => ({ ...f, idEstruturaOrganizacional: estrutura.id, dsEstruturaOrganizacional: estrutura.descricao ?? '' }));
    }

    estruturaLabel(): string {
        const config = this.form();
        const id = config.idEstruturaOrganizacional;
        if (id == null) {
            return '';
        }
        const codigo = String(id);
        const descricao = (config.dsEstruturaOrganizacional ?? '').trim();
        return descricao ? `${codigo} — ${descricao}` : codigo;
    }

    carregarLocaisReuniao(): void {
        this.carregandoLocais.set(true);
        this.configuracaoService
            .listarLocaisReuniao()
            .pipe(
                finalize(() => this.carregandoLocais.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((locais) => {
                this.locaisReuniao.set(locais ?? []);
                this.localFirst.set(0);
            });
    }

    onLocalPageChange(event: PaginatorState): void {
        this.localFirst.set(event.first ?? 0);
        this.localRows.set(event.rows ?? this.localRows());
    }

    aplicarFiltrosLocais(): void {
        this.filtroLocalTextoAplicado.set(this.filtroLocalTexto());
        this.filtroLocalTipoAplicado.set(this.filtroLocalTipo());
        this.filtroLocalSituacaoAplicada.set(this.filtroLocalSituacao());
        this.localFirst.set(0);
    }

    limparFiltrosLocais(): void {
        this.filtroLocalTexto.set('');
        this.filtroLocalTextoAplicado.set('');
        this.filtroLocalTipo.set('');
        this.filtroLocalTipoAplicado.set('');
        this.filtroLocalSituacao.set('');
        this.filtroLocalSituacaoAplicada.set('');
        this.localSortSelecionado.set(this.localSortOpcoes[0]);
        this.localSortField.set(this.localSortOpcoes[0].field);
        this.localSortOrder.set(1);
        this.localFirst.set(0);
    }

    onLocalSortCardChange(opcao: LocalSortOpcao): void {
        this.localSortSelecionado.set(opcao);
        this.localSortField.set(opcao.field);
        this.localSortOrder.set(opcao.direction === 'asc' ? 1 : -1);
        this.localFirst.set(0);
    }

    onLocalSortChange(event: { field?: string | string[] | null; order?: number | null }): void {
        const field = (Array.isArray(event.field) ? event.field[0] : event.field) as LocalSortOpcao['field'] | undefined;
        if (!field) {
            return;
        }
        const order: 1 | -1 = event.order === -1 ? -1 : 1;
        this.localSortField.set(field);
        this.localSortOrder.set(order);
        this.localFirst.set(0);
        const direction = order === 1 ? 'asc' : 'desc';
        const opcao = this.localSortOpcoes.find((o) => o.field === field && o.direction === direction);
        if (opcao) {
            this.localSortSelecionado.set(opcao);
        }
    }

    getTipoLocal(local: ComissaoLocalReuniaoConfiguracaoResponse): 'I' | 'E' {
        if (local.inInternoExterno === 'I' || local.inInternoExterno === 'E') {
            return local.inInternoExterno;
        }
        return local.interno ? 'I' : 'E';
    }

    abrirNovoLocal(): void {
        this.localForm = this.criarLocalFormPadrao();
        this.localDialogVisivel.set(true);
    }

    editarLocal(local: ComissaoLocalReuniaoConfiguracaoResponse): void {
        this.localForm = {
            idLocalReuniao: local.idLocalReuniao ?? null,
            dsLocalReuniao: local.dsLocalReuniao ?? '',
            inInternoExterno: local.inInternoExterno ?? 'I',
            ativo: local.ativo !== false
        };
        this.localDialogVisivel.set(true);
    }

    salvarLocalReuniao(): void {
        const request = this.criarLocalRequest();
        if (!request) {
            return;
        }

        const idLocal = this.localForm.idLocalReuniao;
        const requisicao = idLocal ? this.configuracaoService.atualizarLocalReuniao(idLocal, request) : this.configuracaoService.inserirLocalReuniao(request);
        this.salvandoLocal.set(true);
        requisicao
            .pipe(
                finalize(() => this.salvandoLocal.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                this.localDialogVisivel.set(false);
                this.carregarLocaisReuniao();
                this.messageService.add({ severity: 'success', summary: 'Locais', detail: 'Local salvo com sucesso.' });
            });
    }

    alterarStatusLocal(local: ComissaoLocalReuniaoConfiguracaoResponse, ativo: boolean): void {
        const idLocal = local.idLocalReuniao;
        if (!idLocal) {
            return;
        }

        local.ativo = ativo;
        this.locaisReuniao.set([...this.locaisReuniao()]);
        this.configuracaoService
            .atualizarStatusLocalReuniao(idLocal, { ativo })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Locais',
                        detail: ativo ? 'Local ativado com sucesso.' : 'Local desativado com sucesso.'
                    });
                    this.carregarLocaisReuniao();
                },
                error: () => {
                    local.ativo = !ativo;
                    this.locaisReuniao.set([...this.locaisReuniao()]);
                }
            });
    }

    excluirLocal(local: ComissaoLocalReuniaoConfiguracaoResponse): void {
        const idLocal = local.idLocalReuniao;
        if (!idLocal) {
            return;
        }
        this.localParaExcluir.set(local);
        this.excluirLocalDialogVisivel.set(true);
    }

    confirmarExcluirLocal(): void {
        const idLocal = this.localParaExcluir()?.idLocalReuniao;
        if (!idLocal) {
            return;
        }

        this.configuracaoService
            .excluirLocalReuniao(idLocal)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.excluirLocalDialogVisivel.set(false);
                this.localParaExcluir.set(null);
                this.carregarLocaisReuniao();
                this.messageService.add({ severity: 'success', summary: 'Locais', detail: 'Local excluído com sucesso.' });
            });
    }

    private criarFormPadrao(): ComissaoConfiguracaoForm {
        return {
            idEstruturaOrganizacional: 35,
            dsEstruturaOrganizacional: '',
            nmEmailRemetente: '',
            senhaEmailRemetente: '',
            limparSenhaEmail: false,
            idCargoAssinante: null,
            dsCargoAssinante: '',
            idSolicitanteAssinante: null,
            ufSgUfAssinante: '',
            nmSolicitanteAssinante: '',
            nmEmailsCopia: '',
            copiaConvocacoes: false,
            copiaConvites: false,
            copiaConvitesOficiados: false,
            copiaCancelamentos: false,
            tituloEmailConvocacoes: '',
            corpoEmailConvocacoes: '',
            tituloEmailConvites: '',
            corpoEmailConvites: '',
            tituloEmailCancelamentos: '',
            corpoEmailCancelamentos: '',
            autosave: true,
            temporizadorAutosave: 1,
            idAssuntoAta: '',
            dsAssuntoAta: ''
        };
    }

    private criarEditorEmailPadrao(): EditorEmailState {
        return {
            visivel: false,
            titulo: '',
            campo: null,
            texto: ''
        };
    }

    private criarLocalFormPadrao(): LocalReuniaoForm {
        return {
            idLocalReuniao: null,
            dsLocalReuniao: '',
            inInternoExterno: 'I',
            ativo: true
        };
    }

    private aplicarConfiguracao(configuracao: ComissaoConfiguracaoResponse): void {
        this.senhaConfigurada.set(!!configuracao.senhaEmailConfigurada);
        const atual = this.form();
        this.form.set({
            idEstruturaOrganizacional: configuracao.idEstruturaOrganizacional ?? atual.idEstruturaOrganizacional,
            dsEstruturaOrganizacional: configuracao.dsEstruturaOrganizacional ?? '',
            nmEmailRemetente: configuracao.nmEmailRemetente ?? '',
            senhaEmailRemetente: '',
            limparSenhaEmail: false,
            idCargoAssinante: configuracao.idCargoAssinante ?? null,
            dsCargoAssinante: configuracao.dsCargoAssinante ?? '',
            idSolicitanteAssinante: configuracao.idSolicitanteAssinante ?? null,
            ufSgUfAssinante: configuracao.ufSgUfAssinante ?? '',
            nmSolicitanteAssinante: configuracao.nmSolicitanteAssinante ?? '',
            nmEmailsCopia: configuracao.nmEmailsCopia ?? '',
            copiaConvocacoes: !!configuracao.copiaConvocacoes,
            copiaConvites: !!configuracao.copiaConvites,
            copiaConvitesOficiados: !!configuracao.copiaConvitesOficiados,
            copiaCancelamentos: !!configuracao.copiaCancelamentos,
            tituloEmailConvocacoes: configuracao.tituloEmailConvocacoes ?? '',
            corpoEmailConvocacoes: configuracao.corpoEmailConvocacoes ?? '',
            tituloEmailConvites: configuracao.tituloEmailConvites ?? '',
            corpoEmailConvites: configuracao.corpoEmailConvites ?? '',
            tituloEmailCancelamentos: configuracao.tituloEmailCancelamentos ?? '',
            corpoEmailCancelamentos: configuracao.corpoEmailCancelamentos ?? '',
            autosave: configuracao.autosave !== false,
            temporizadorAutosave: configuracao.temporizadorAutosave ?? 1,
            idAssuntoAta: configuracao.idAssuntoAta ?? '',
            dsAssuntoAta: configuracao.dsAssuntoAta ?? ''
        });
    }

    private aplicarAssinante(assinante: ComissaoAssinanteResponse): void {
        this.form.update((f) => ({
            ...f,
            idCargoAssinante: assinante.idCargo ?? f.idCargoAssinante,
            dsCargoAssinante: assinante.dsCargo ?? f.dsCargoAssinante,
            idSolicitanteAssinante: assinante.idSolicitante ?? null,
            ufSgUfAssinante: assinante.ufSgUf ?? '',
            nmSolicitanteAssinante: assinante.nmSolicitante ?? ''
        }));
    }

    private criarRequest(): ComissaoConfiguracaoRequest {
        const f = this.form();
        return {
            nmEmailRemetente: this.normalizarTexto(f.nmEmailRemetente),
            senhaEmailRemetente: this.normalizarTexto(f.senhaEmailRemetente),
            limparSenhaEmail: f.limparSenhaEmail,
            idSolicitanteAssinante: f.idSolicitanteAssinante,
            ufSgUfAssinante: this.normalizarTexto(f.ufSgUfAssinante)?.toUpperCase() ?? null,
            nmEmailsCopia: this.normalizarTexto(f.nmEmailsCopia),
            copiaConvocacoes: f.copiaConvocacoes,
            copiaConvites: f.copiaConvites,
            copiaConvitesOficiados: f.copiaConvitesOficiados,
            copiaCancelamentos: f.copiaCancelamentos,
            tituloEmailConvocacoes: this.normalizarTexto(f.tituloEmailConvocacoes),
            corpoEmailConvocacoes: this.normalizarTexto(f.corpoEmailConvocacoes),
            tituloEmailConvites: this.normalizarTexto(f.tituloEmailConvites),
            corpoEmailConvites: this.normalizarTexto(f.corpoEmailConvites),
            tituloEmailCancelamentos: this.normalizarTexto(f.tituloEmailCancelamentos),
            corpoEmailCancelamentos: this.normalizarTexto(f.corpoEmailCancelamentos),
            autosave: f.autosave,
            temporizadorAutosave: f.autosave ? f.temporizadorAutosave : null,
            idAssuntoAta: this.normalizarTexto(f.idAssuntoAta)
        };
    }

    private criarLocalRequest(): ComissaoLocalReuniaoRequest | null {
        const dsLocalReuniao = this.normalizarTexto(this.localForm.dsLocalReuniao);
        if (!dsLocalReuniao) {
            this.messageService.add({ severity: 'warn', summary: 'Locais', detail: 'Informe a descrição do local.' });
            return null;
        }
        return {
            dsLocalReuniao,
            inInternoExterno: this.localForm.inInternoExterno === 'E' ? 'E' : 'I',
            ativo: this.localForm.ativo
        };
    }

    private validarEstrutura(): number | null {
        const idEstrutura = Number(this.form().idEstruturaOrganizacional);
        if (!Number.isFinite(idEstrutura) || idEstrutura <= 0) {
            this.messageService.add({ severity: 'warn', summary: 'Configurações', detail: 'Informe a estrutura organizacional.' });
            return null;
        }
        return idEstrutura;
    }

    private validarAutosave(): boolean {
        const f = this.form();
        if (f.autosave && (!f.temporizadorAutosave || f.temporizadorAutosave < 1)) {
            this.messageService.add({ severity: 'warn', summary: 'Configurações', detail: 'O intervalo de autosave deve ser maior ou igual a um minuto.' });
            return false;
        }
        return true;
    }

    private normalizarTexto(valor: string | null | undefined): string | null {
        const texto = (valor ?? '').trim();
        return texto ? texto : null;
    }
}
